import { Link, LinkComponentProps } from '@tanstack/react-router'
import {
  Cell,
  Header,
  Row,
  RowData,
  Table as TableType,
  flexRender,
} from '@tanstack/react-table'

import {
  Icon,
  Skeleton,
  TableSortIndicator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@hap/components'

interface TableProps<D> {
  table: TableType<D>
  verticalLines?: boolean
  getRowLink?: (row: Row<D>) => LinkComponentProps | null
  isLoading?: boolean
}

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    highlight?: (cell: Cell<TData, TValue>) => Record<string, boolean>
    align?: 'left' | 'center' | 'right'
    tooltip?: string
    disableRowLink?: boolean
    onClick?: (cell: Cell<TData, TValue>) => Record<string, boolean>
    // OLD
    sticky?: boolean // @deprecated sticky
    width?: number | string // @deprecated width
    minWidth?: number | string // @deprecated minWidth
  }
}

export function createPlacehodlerData<D>(): D[] {
  return [{}, {}, {}] as unknown as D[]
}

export function Table<D>(props: TableProps<D>) {
  function renderColumnHeader<D>(header: Header<D, unknown>) {
    if (
      header.column.columnDef.meta &&
      'tooltip' in header.column.columnDef.meta &&
      typeof header.column.columnDef.meta.tooltip === 'string'
    ) {
      return (
        <Tooltip>
          <TooltipTrigger className="flex flex-wrap items-center gap-1">
            {flexRender(header.column.columnDef.header, header.getContext())}{' '}
            <Icon name="info" className="h-4 opacity-60" />
          </TooltipTrigger>
          <TooltipContent className="max-w-[300px]">
            {header.column.columnDef.meta.tooltip}
          </TooltipContent>
        </Tooltip>
      )
    }
    return flexRender(header.column.columnDef.header, header.getContext())
  }

  return (
    <table className="w-full">
      <thead>
        {props.table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className="border-t">
            {headerGroup.headers.map((header) => {
              return (
                <th
                  key={header.id}
                  data-sorting={!!header.column.getIsSorted()}
                  className={cn(
                    'text-accent-foreground data-[sorting=true]:border-b-foreground data-[sorting=true]:text-foreground/90 group px-1 py-1.5 text-left font-normal text-nowrap data-[sorting=true]:font-semibold',
                    header.column.getCanSort() ? 'cursor-pointer' : '',
                    props.verticalLines && 'border-r px-4 last:border-r-0',
                  )}
                  style={{
                    width: header.getSize() === 0 ? 'auto' : header.getSize(),
                    minWidth: header.column.columnDef.minSize,
                    maxWidth: header.column.columnDef.maxSize,
                  }}
                >
                  {header.column.getCanSort() ? (
                    <div
                      className="group-hover:bg-accent hover:text-foreground/80 -ml-1 inline-flex cursor-pointer items-center rounded p-1"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {renderColumnHeader(header)}
                      <TableSortIndicator sort={header.column.getIsSorted()} />
                    </div>
                  ) : (
                    renderColumnHeader(header)
                  )}
                </th>
              )
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {props.table.getRowModel().rows.map((row) => {
          const rowLink = props.getRowLink?.(row)
          return (
            <tr
              key={row.id}
              data-selected={row.getIsSelected()}
              className={cn(
                'group/row data-[selected=true]:bg-accent/50 data-[selected=true]:hover:bg-accent/50 hover:bg-accent/40 border-t transition-colors duration-150',
                rowLink && 'hover:bg-accent/60 relative cursor-pointer',
                {
                  'line-through opacity-50':
                    'active' in (row.original as Record<string, unknown>) &&
                    // @ts-ignore
                    (row.original.active as unknown as boolean) === false,
                },
              )}
            >
              {row.getVisibleCells().map((cell) => {
                const classNames = cn(
                  'relative px-1 py-2 text-left flex',
                  props.verticalLines && 'px-4',
                )

                const highlightConfig = cell.column.columnDef.meta?.highlight
                const highlightClassNames = highlightConfig ? highlightConfig(cell) : null
                return (
                  <td
                    key={cell.id}
                    className={cn(
                      'group/cell p-0',
                      props.verticalLines && 'border-r last:border-r-0',
                      highlightClassNames,
                    )}
                    style={{
                      width: cell.column.getSize() === 0 ? 'auto' : cell.column.getSize(),
                      minWidth: cell.column.columnDef.minSize,
                      maxWidth: cell.column.columnDef.maxSize,
                    }}
                  >
                    {props.isLoading ? (
                      <div className={classNames}>
                        <Skeleton loading className="block w-full max-w-40" />
                      </div>
                    ) : rowLink && !cell.column.columnDef.meta?.disableRowLink ? (
                      <Link
                        {...rowLink}
                        className={classNames}
                        aria-label="View contact log details"
                        onClick={(e) => {
                          const target = e.target as HTMLElement
                          const link = e.currentTarget as HTMLElement

                          // Find the nearest interactive child
                          const interactiveChild = target.closest(
                            'a, button, input, select, textarea, [role="button"]',
                          )

                          // Block ONLY if that interactive element is NOT the Link itself
                          if (
                            (interactiveChild && interactiveChild !== link) ||
                            // NOTE: Added null check to capture clicking on an element that is in a popover, select, or dropdown
                            interactiveChild === null
                          ) {
                            e.preventDefault()
                            e.stopPropagation()
                          }
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}&nbsp;
                      </Link>
                    ) : (
                      <div className={classNames}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    )}
                  </td>
                )
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
